// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
import { useRef, useState } from "react"
import type { SetEntry } from "@/lib/types"
import { REP_STEP, WEIGHT_STEP, nextSet, duplicateLastSet } from "@/lib/sets"
import SetStepper from "./SetStepper"

export default function SetList({ sets, onChange, doneFlags, onToggleDone, hideWeight, repsLabel }: {
  sets: SetEntry[]
  onChange: (sets: SetEntry[]) => void
  /** Optional (geführtes Training): Haken-Spalte pro Satz. Ohne beide Props ändert sich nichts. */
  doneFlags?: boolean[]
  onToggleDone?: (index: number) => void
  /** Körpergewichtsübung (A3): Gewichtsspalte weg, Zusatzgewicht auf Wunsch.
   *  Aufrufer keyen die Liste per Übungs-Id, damit der Schalter zurückspringt. */
  hideWeight?: boolean
  /** Zeit-Übungen (Runde 11): „Min."/„Sek." statt „Wdh." */
  repsLabel?: string
}) {
  const [showExtra, setShowExtra] = useState(false)
  const update = (i: number, patch: Partial<SetEntry>) =>
    onChange(sets.map((s, j) => (j === i ? { ...s, ...patch } : s)))

  const withCheck = Boolean(doneFlags && onToggleDone)
  // Gewichte aus dem letzten Eintrag (z. B. Zusatzgewicht) bleiben sichtbar
  const weightHidden = Boolean(hideWeight) && !showExtra && !sets.some((s) => s.weight_kg > 0)
  const mods = `${withCheck ? " with-check" : ""}${weightHidden ? " no-weight" : ""}`

  return (
    <>
      <div className={`collabel${mods}`} aria-hidden="true">
        <span>#</span><span>{repsLabel ?? "Wdh."}</span>{!weightHidden && <span>Gewicht (kg)</span>}<span></span>
      </div>
      {sets.map((s, i) => (
        <div className="setwrap" key={i}>
          <div className={`setrow enter${mods}`}>
            {withCheck ? (
              <button
                type="button"
                className={`setdone${doneFlags![i] ? " on" : ""}`}
                aria-pressed={doneFlags![i] ?? false}
                aria-label={doneFlags![i] ? `Satz ${i + 1} wieder öffnen` : `Satz ${i + 1} abhaken`}
                onClick={() => onToggleDone!(i)}
              >{doneFlags![i] ? "✓" : i + 1}</button>
            ) : (
              <div className="num">{i + 1}</div>
            )}
            <SetStepper value={s.reps} step={REP_STEP} onChange={(v) => update(i, { reps: v })} />
            {!weightHidden && (
              <SetStepper value={s.weight_kg} step={WEIGHT_STEP} onChange={(v) => update(i, { weight_kg: v })} />
            )}
            <button
              type="button"
              className="del"
              aria-label={`Satz ${i + 1} entfernen`}
              onClick={() => { if (sets.length > 1) onChange(sets.filter((_, j) => j !== i)) }}
            >✕</button>
          </div>
          {withCheck && doneFlags![i] && (
            <GeschafftBalken
              nr={i + 1}
              satz={s}
              zeigeGewicht={!weightHidden}
              einheit={repsLabel ?? "Wdh."}
              onWeg={() => onToggleDone!(i)}
            />
          )}
        </div>
      ))}
      <div className="addset">
        <button type="button" onClick={() => onChange([...sets, nextSet(sets)])}>+ Satz</button>
        <button type="button" title="Letzten Satz kopieren" onClick={() => onChange([...sets, duplicateLastSet(sets)])}>
          + Satz wie zuvor
        </button>
      </div>
      {weightHidden && (
        <button type="button" className="extraweight" onClick={() => setShowExtra(true)}>
          Zusatzgewicht eintragen
        </button>
      )}
    </>
  )
}

/** Legt sich über einen abgehakten Satz („geschafft") und lässt sich zur Seite
 *  wischen, wenn man den Satz doch noch ändern will (Thors Feedback Runde 11).
 *  Tippen tut dasselbe — für Maus und Menschen, die nicht wischen mögen. */
function GeschafftBalken({ nr, satz, zeigeGewicht, einheit, onWeg }: {
  nr: number
  satz: SetEntry
  zeigeGewicht: boolean
  einheit: string
  onWeg: () => void
}) {
  const [dx, setDx] = useState(0)
  const startX = useRef<number | null>(null)

  const wert = zeigeGewicht && satz.weight_kg
    ? `${satz.reps} × ${satz.weight_kg} kg`
    : `${satz.reps} ${einheit}`

  return (
    <button
      type="button"
      className="setdone-bar"
      style={dx ? { transform: `translateX(${dx}px)`, opacity: Math.max(0.35, 1 - Math.abs(dx) / 220) } : undefined}
      aria-label={`Satz ${nr} geschafft – zum Ändern wegwischen oder tippen`}
      onClick={onWeg}
      onTouchStart={(e) => { startX.current = e.touches[0].clientX }}
      onTouchMove={(e) => {
        if (startX.current === null) return
        setDx(e.touches[0].clientX - startX.current)
      }}
      onTouchEnd={() => {
        if (Math.abs(dx) > 70) onWeg()
        startX.current = null
        setDx(0)
      }}
    >
      <span className="sdb-check" aria-hidden="true">✓</span>
      <span className="sdb-txt">Satz {nr} geschafft</span>
      <span className="sdb-wert">{wert}</span>
    </button>
  )
}
